import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Autocomplete,
  TextField,
  Grid2 as Grid,
  FormControl,
  Grid2Props,
  Typography,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { singleGridItem } from "../defaultStyles";
import { useFormHandlerContext } from "../../../../controllers/formController";
import { useInputFullName } from "../subForms/NamePrefixContext";
import UserAttributeMetadataPaper from "../../attributeMetadata/UserAttributeMetadataPaper";
import FormLabel from "../../common/base/FormLabel";
import { useFetchUserEnums } from "../../../../hooks/query/useUserEnumQuery";

export interface FormAutoCompleteFieldProps extends Grid2Props {
  name?: string;
  disabled?: boolean;
  label: string;
  options?: { value: string; label: string }[];
  useEnum?: string;
  required?: boolean;
  multiple?: boolean;
  placeholder?: string;
  freeSolo?: boolean;
  selfFetching?: boolean;
  validate?: (value: any) => string | boolean;
}

const FormAutoCompleteField: React.FC<FormAutoCompleteFieldProps> = ({
  name,
  label,
  options,
  useEnum,
  required,
  disabled,
  multiple = false,
  placeholder,
  freeSolo = false,
  selfFetching = false,
  validate,
  ...grid
}) => {
  const { isProcessing, userEnums } = useFormHandlerContext();
  const { control } = useFormContext();
  const fullName = useInputFullName(name);
  const fetchUserEnums = useFetchUserEnums(
    selfFetching && useEnum ? [useEnum] : []
  );

  const inferredOptions =
    (useEnum && (userEnums[useEnum] || fetchUserEnums.data)) || [];

  const finalOptions = (
    options ||
    inferredOptions.map((option) => ({
      value: option,
      label: option,
    }))
  ).filter((option) => (multiple ? Boolean(option.value) : true));

  const getOptionFromValue = (
    value: string
  ): { value: string; label: string } | null =>
    value
      ? finalOptions.find((option) => option.value === value) ||
        (freeSolo ? { value, label: value.toString() } : null)
      : null;

  return (
    <Grid {...singleGridItem} {...grid}>
      <UserAttributeMetadataPaper name={fullName}>
        <Controller
          name={fullName}
          control={control}
          rules={{
            required: required ? `${label} is required` : false,
            validate,
          }}
          render={({ field, fieldState }) => (
            <>
              <FormLabel required={required} label={label} name={fullName} />
              <FormControl fullWidth error={!!fieldState.error}>
                <Autocomplete
                  multiple={multiple}
                  freeSolo={freeSolo}
                  disabled={disabled || isProcessing}
                  options={finalOptions}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.value === (value?.value ?? value)
                  }
                  value={
                    multiple
                      ? (field.value || [])
                          .map(getOptionFromValue)
                          .filter(Boolean)
                      : getOptionFromValue(field.value)
                  }
                  onChange={(_, newValue) => {
                    const newValues = multiple
                      ? newValue
                          .map((option: { value: any }) =>
                            typeof option === "string"
                              ? option
                              : option.value || null
                          )
                          .filter(Boolean)
                      : newValue?.value || null;
                    field.onChange(newValues);
                  }}
                  onInputChange={
                    freeSolo && !multiple
                      ? (_, inputValue) => {
                          field.onChange(inputValue);
                        }
                      : undefined
                  }
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      value={option.value}
                      key={option.value}
                    >
                      {option.value ? (
                        option.label
                      ) : (
                        <Typography fontStyle="italic" color="text.secondary">
                          {option.label || "None"}
                        </Typography>
                      )}
                    </MenuItem>
                  )}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        placeholder={placeholder}
                        required={required}
                        error={!!fieldState.error}
                        helperText={
                          <>
                            {fieldState.error && (
                              <FormHelperText>
                                {fieldState.error?.message}
                              </FormHelperText>
                            )}
                            {selfFetching && fetchUserEnums.isLoading && (
                              <FormHelperText>
                                Loading options...
                              </FormHelperText>
                            )}
                            {selfFetching && fetchUserEnums.data && (
                              <FormHelperText>
                                Failed to fetch options.&nbsp;
                                <Typography
                                  variant="caption"
                                  component="span"
                                  sx={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() => fetchUserEnums.refetch()}
                                >
                                  Try again
                                </Typography>
                                .
                              </FormHelperText>
                            )}
                          </>
                        }
                        fullWidth
                      />
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                      minWidth: 200,
                    },
                  }}
                />
              </FormControl>
            </>
          )}
        />
      </UserAttributeMetadataPaper>
    </Grid>
  );
};

export default FormAutoCompleteField;
